<template>
    <div class="glass-container">
        <div class="tax-exempt-companies-container">
            <!-- Main Card -->
            <div class="modern-card">
                <!-- Header Section -->
                <div class="header-section">
                    <div class="title-section">
                        <h1 class="page-title">Tax Exempt Companies</h1>
                        <p class="page-subtitle">Manage tax exempt company information and certificates</p>
                    </div>
                    
                    <div class="action-buttons">
                        <b-button @click="add_new" variant="primary" class="action-btn add-btn">
                            <b-icon icon="plus-circle" class="mr-2"></b-icon>
                            Add Company
                        </b-button>
                        <b-button @click="fetch_init" variant="outline-secondary" class="action-btn refresh-btn">
                            <b-icon icon="arrow-clockwise" class="mr-2"></b-icon>
                            Refresh
                        </b-button>
                    </div>
                </div>

                <!-- Info Alert -->
                <b-alert variant="info" show class="info-alert">
                    <div class="alert-content">
                        <b-icon icon="info-circle" class="mr-2"></b-icon>
                        <div>
                            <strong>Important:</strong> This tool contains list of tax exempt companies in the system. 
                            You can add new tax exempt companies here. Make sure to upload the tax exemption certificate 
                            for each company.
                        </div>
                    </div>
                </b-alert>

                <!-- Loading State -->
                <div v-if="items_being_loaded" class="loading-container">
                    <loading 
                        :active="items_being_loaded"
                        :can-cancel="false"
                        :is-full-page="false"
                    ></loading>
                </div>

                <!-- Tax Exempt Companies List -->
                <div v-else class="companies-list-container">
                    <div v-if="items.length > 0">
                        <div v-for="(item, idx) in items" :key="idx" class="company-card">
                            <!-- Company Basic Info -->
                            <div class="company-header">
                                <div class="company-status">
                                    <span class="field-label">STATUS</span>
                                    <div class="status-toggle">
                                        <b-form-checkbox
                                            v-model="item.is_active"
                                            switch
                                            size="lg"
                                            class="status-switch"
                                            @change="toggleCompanyStatus(item)"
                                        >
                                            <span :class="{'text-success': item.is_active, 'text-danger': !item.is_active}" class="status-text">
                                                {{ item.is_active ? 'ACTIVE' : 'INACTIVE' }}
                                            </span>
                                        </b-form-checkbox>
                                    </div>
                                </div>
                                <div class="company-name">
                                    <span class="field-label">COMPANY NAME</span>
                                    <b-input 
                                        v-model="item.finance_company_name" 
                                        size="sm" 
                                        class="name-input"
                                        placeholder="Enter company name"
                                        :class="{ 'new-company': !item.finance_company_id }"
                                    ></b-input>
                                </div>
                                
                                <div class="company-phone">
                                    <span class="field-label">PHONE NUMBER</span>
                                    <b-input 
                                        v-model="item.finance_company_phone_number" 
                                        size="sm" 
                                        class="phone-input"
                                        placeholder="Phone number"
                                    ></b-input>
                                </div>
                                
                                <div class="company-ein">
                                    <span class="field-label">EIN</span>
                                    <b-input 
                                        v-model="item.ein" 
                                        size="sm" 
                                        class="ein-input"
                                        placeholder="Employer Identification Number"
                                    ></b-input>
                                </div>
                            </div>

                            <!-- Company Contact Details -->
                            <div class="company-details-row">
                                <div class="detail-field">
                                    <span class="field-label">WEBSITE</span>
                                    <b-input 
                                        v-model="item.finance_company_website" 
                                        size="sm" 
                                        class="website-input"
                                        placeholder="https://example.com"
                                    ></b-input>
                                </div>
                                
                                <div class="detail-field address-field">
                                    <span class="field-label">ADDRESS</span>
                                    <b-input 
                                        v-model="item.finance_company_address" 
                                        size="sm" 
                                        class="address-input"
                                        placeholder="Company address"
                                    ></b-input>
                                </div>
                            </div>

                            <!-- Password Section -->
                            <div class="password-section">
                                <div class="password-field">
                                    <span class="field-label">PASSWORD</span>
                                    <div class="password-input-group">
                                        <b-input 
                                            v-model="item.password" 
                                            :type="item.showPassword ? 'text' : 'password'"
                                            size="sm" 
                                            class="password-input"
                                            placeholder="Leave blank to keep current"
                                        ></b-input>
                                        <div class="show-password-toggle">
                                            <b-form-checkbox
                                                v-model="item.showPassword"
                                                class="show-password-checkbox"
                                            >
                                                Show
                                            </b-form-checkbox>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Tax Certificate Section -->
                            <div class="certificate-section">
                                <div class="certificate-header">
                                    <b-icon icon="file-earmark-pdf" class="mr-2"></b-icon>
                                    <h6 class="certificate-title">Tax Exemption Certificate</h6>
                                </div>
                                
                                <div class="certificate-container">
                                    <div v-if="!item.certificate" class="certificate-upload">
                                        <b-form-file
                                            v-model="item.pdfFile"
                                            accept=".pdf"
                                            placeholder="Choose PDF certificate..."
                                            drop-placeholder="Drop PDF here..."
                                            @change="(event) => handlePdfUpload(item, event)"
                                            class="certificate-file-input"
                                        >
                                            <template #file-name="{ names }">
                                                <span class="text-truncate">{{ names[0] }}</span>
                                                <span v-if="names.length > 1" class="text-nowrap">
                                                    (+ {{ names.length - 1 }} more files)
                                                </span>
                                            </template>
                                        </b-form-file>
                                        <small class="upload-hint">Max file size: 5MB, PDF only</small>
                                    </div>
                                    
                                    <div v-else class="certificate-actions">
                                        <div class="certificate-buttons">
                                            <b-button 
                                                @click="viewPdfInNewWindow(item.certificate)" 
                                                variant="outline-info" 
                                                size="sm"
                                                class="certificate-btn preview-btn"
                                            >
                                                <b-icon icon="eye" class="mr-1"></b-icon>
                                                Preview
                                            </b-button>
                                            <b-button 
                                                @click="downloadPdf(item.certificate, item.finance_company_name)" 
                                                variant="outline-success" 
                                                size="sm"
                                                class="certificate-btn download-btn"
                                            >
                                                <b-icon icon="download" class="mr-1"></b-icon>
                                                Download
                                            </b-button>
                                            <b-button 
                                                @click="removePdf(item)" 
                                                variant="outline-danger" 
                                                size="sm"
                                                class="certificate-btn remove-btn"
                                            >
                                                <b-icon icon="trash" class="mr-1"></b-icon>
                                                Remove
                                            </b-button>
                                        </div>
                                        <div class="certificate-status">
                                            <span class="certificate-uploaded">
                                                <b-icon icon="check-circle" variant="success" class="mr-1"></b-icon>
                                                Certificate uploaded
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Tax Collection Section -->
                            <div class="tax-section">
                                <div class="tax-header">
                                    <b-icon icon="building" class="mr-2"></b-icon>
                                    <h6 class="tax-title">Sales Tax Collection by State</h6>
                                </div>
                                <p class="tax-description">
                                    Finance company will collect sales tax in checked states:
                                </p>
                                
                                <div class="states-container">
                                    <div class="states-header">
                                        <span class="states-count">
                                            Selected: {{ item.finance_company_charges_tax_in_states ? item.finance_company_charges_tax_in_states.length : 0 }} states
                                        </span>
                                        <b-button 
                                            @click="toggleAllStates(item)" 
                                            variant="outline-secondary" 
                                            size="sm"
                                            class="toggle-all-btn"
                                        >
                                            {{ getToggleAllButtonText(item) }}
                                        </b-button>
                                    </div>
                                    
                                    <div class="states-grid">
                                        <div 
                                            v-for="state in states" 
                                            :key="state.value"
                                            class="state-checkbox-item"
                                            :class="{ 'selected': isStateSelected(item, state.value) }"
                                            @click="toggleState(item, state.value)"
                                        >
                                            <b-form-checkbox
                                                :checked="isStateSelected(item, state.value)"
                                                class="state-checkbox"
                                            >
                                                <span class="state-label">{{ state.text }}</span>
                                            </b-form-checkbox>
                                        </div>
                                    </div>
                                    
                                    <div class="selected-states-preview" v-if="item.finance_company_charges_tax_in_states && item.finance_company_charges_tax_in_states.length > 0">
                                        <span class="preview-label">Selected:</span>
                                        <span class="selected-states">
                                            {{ getSelectedStatesPreview(item) }}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <!-- Action Buttons -->
                            <div class="company-actions">
                                <div class="company-info" v-if="item.finance_company_id">
                                    <span class="company-id">ID: {{ item.finance_company_id }}</span>
                                    <span class="company-type-badge">
                                        <b-badge variant="success">Tax Exempt</b-badge>
                                    </span>
                                </div>
                                
                                <div class="action-buttons-wrapper">
                                    <b-button 
                                        @click="save_finance_company(item)" 
                                        variant="success" 
                                        size="sm"
                                        class="action-btn save-btn"
                                        :disabled="!item.finance_company_name"
                                    >
                                        <b-icon icon="check" class="mr-1"></b-icon>
                                        Save
                                    </b-button>
                                    
                                    <b-button 
                                        @click="remove_finance_company(idx)" 
                                        variant="outline-danger" 
                                        size="sm"
                                        class="action-btn cancel-btn"
                                        v-if="!item.finance_company_id"
                                    >
                                        <b-icon icon="x" class="mr-1"></b-icon>
                                        Cancel
                                    </b-button>
                                </div>
                            </div>
                        </div>
                        
                        <!-- Table Footer -->
                        <div class="table-footer">
                            <div class="pagination-info">
                                Showing {{ items.length }} tax exempt companies
                            </div>
                        </div>
                    </div>
                    
                    <!-- Empty State -->
                    <div v-else class="empty-state">
                        <div class="empty-state-icon">
                            <b-icon icon="building" font-scale="3"></b-icon>
                        </div>
                        <h3>No Tax Exempt Companies Found</h3>
                        <p>No tax exempt companies in the system yet</p>
                        <div class="empty-state-actions mt-3">
                            <b-button @click="add_new" variant="primary">
                                <b-icon icon="plus-circle" class="mr-2"></b-icon>
                                Add First Company
                            </b-button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import Requestor from '@/utilities/Requestor'
    import Loading from 'vue-loading-overlay';
    import 'vue-loading-overlay/dist/vue-loading.css';

    export default {
        name: "ListTaxExemptCompanies",
        components: { Loading },
        data: function() {
            return {
                packet: {},
                items: [],
                items_being_loaded: false,
                states: [
                    {value: 'PA', text: 'PA'},
                    {value: 'NJ', text: 'NJ'},
                    {value: 'DE', text: 'DE'},
                    {value: 'AL', text: 'AL'},
                    {value: 'AK', text: 'AK'},
                    {value: 'AS', text: 'AS'},
                    {value: 'AZ', text: 'AZ'},
                    {value: 'AR', text: 'AR'},
                    {value: 'CA', text: 'CA'},
                    {value: 'CO', text: 'CO'},
                    {value: 'CT', text: 'CT'},
                    {value: 'DC', text: 'DC'},
                    {value: 'FM', text: 'FM'},
                    {value: 'FL', text: 'FL'},
                    {value: 'GA', text: 'GA'},
                    {value: 'GU', text: 'GU'},
                    {value: 'HI', text: 'HI'},
                    {value: 'ID', text: 'ID'},
                    {value: 'IL', text: 'IL'},
                    {value: 'IN', text: 'IN'},
                    {value: 'IA', text: 'IA'},
                    {value: 'KS', text: 'KS'},
                    {value: 'KY', text: 'KY'},
                    {value: 'LA', text: 'LA'},
                    {value: 'ME', text: 'ME'},
                    {value: 'MH', text: 'MH'},
                    {value: 'MD', text: 'MD'},
                    {value: 'MA', text: 'MA'},
                    {value: 'MI', text: 'MI'},
                    {value: 'MN', text: 'MN'},
                    {value: 'MS', text: 'MS'},
                    {value: 'MO', text: 'MO'},
                    {value: 'MT', text: 'MT'},
                    {value: 'NE', text: 'NE'},
                    {value: 'NV', text: 'NV'},
                    {value: 'NH', text: 'NH'},
                    {value: 'NM', text: 'NM'},
                    {value: 'NY', text: 'NY'},
                    {value: 'NC', text: 'NC'},
                    {value: 'ND', text: 'ND'},
                    {value: 'MP', text: 'MP'},
                    {value: 'OH', text: 'OH'},
                    {value: 'OK', text: 'OK'},
                    {value: 'OR', text: 'OR'},
                    {value: 'PW', text: 'PW'},
                    {value: 'PR', text: 'PR'},
                    {value: 'RI', text: 'RI'},
                    {value: 'SC', text: 'SC'},
                    {value: 'SD', text: 'SD'},
                    {value: 'TN', text: 'TN'},
                    {value: 'TX', text: 'TX'},
                    {value: 'UT', text: 'UT'},
                    {value: 'VT', text: 'VT'},
                    {value: 'VI', text: 'VI'},
                    {value: 'VA', text: 'VA'},
                    {value: 'WA', text: 'WA'},
                    {value: 'WV', text: 'WV'},
                    {value: 'WI', text: 'WI'},
                    {value: 'WY', text: 'WY'},
                ],
            }
        },
        methods: {
            toggleCompanyStatus(item) {
                // Save the company with the updated status
                this.save_finance_company(item);
                
                // Show a toast notification
                let statusText = item.is_active ? 'activated' : 'deactivated';
                this.$bvToast.toast(
                    `Company ${item.finance_company_name} has been ${statusText}.`, 
                    {
                        title: "Status Updated", 
                        autoHideDelay: 1500, 
                        variant: item.is_active ? "success" : "warning"
                    }
                );
            },
            
            // PDF Upload Handler
            handlePdfUpload(item, event) {
                const file = event.target.files[0];
                if (!file) return;

                // Validate file type
                if (file.type !== 'application/pdf') {
                    this.$bvToast.toast(
                        "Please upload a PDF file only.", 
                        {title: "Error", autoHideDelay: 1500, variant:"warning"}
                    );
                    item.pdfFile = null;
                    return;
                }

                // Validate file size (5MB limit)
                if (file.size > 5 * 1024 * 1024) {
                    this.$bvToast.toast(
                        "PDF file size should be less than 5MB.", 
                        {title: "Error", autoHideDelay: 1500, variant:"warning"}
                    );
                    item.pdfFile = null;
                    return;
                }

                this.items_being_loaded = true;
                
                // Convert PDF to base64 for storage
                const reader = new FileReader();
                reader.onload = (e) => {
                    const base64 = e.target.result;
                    // Store base64 string in certificate field
                    item.certificate = base64;
                    item.pdfFile = null;
                    this.items_being_loaded = false;
                    
                    this.$bvToast.toast(
                        "Tax exemption certificate uploaded successfully!", 
                        {title: "Success", autoHideDelay: 1500, variant:"success"}
                    );
                };
                reader.onerror = () => {
                    this.items_being_loaded = false;
                    this.$bvToast.toast(
                        "Error reading PDF file.", 
                        {title: "Error", autoHideDelay: 1500, variant:"warning"}
                    );
                };
                reader.readAsDataURL(file);
            },

            // View PDF in new window
            viewPdfInNewWindow(base64String) {
                try {
                    // Check if base64String is valid
                    if (!base64String) {
                        this.$bvToast.toast(
                            "No certificate found.", 
                            {title: "Error", autoHideDelay: 1500, variant:"warning"}
                        );
                        return;
                    }

                    // Ensure it's a proper data URL
                    let pdfData = base64String;
                    if (!pdfData.startsWith('data:application/pdf')) {
                        if (pdfData.startsWith('data:')) {
                            pdfData = pdfData.replace(/^data:[^;]*;base64,/, 'data:application/pdf;base64,');
                        } else {
                            pdfData = `data:application/pdf;base64,${pdfData}`;
                        }
                    }

                    // Create a new window for PDF viewing
                    const pdfWindow = window.open('', '_blank');
                    if (pdfWindow) {
                        pdfWindow.document.write(`
                            <!DOCTYPE html>
                            <html>
                            <head>
                                <title>Tax Exemption Certificate</title>
                                <style>
                                    body, html { margin: 0; padding: 0; height: 100%; overflow: hidden; }
                                    .container { width: 100%; height: 100vh; }
                                    iframe, embed { width: 100%; height: 100%; border: none; }
                                    .fallback { padding: 20px; text-align: center; }
                                </style>
                            </head>
                            <body>
                                <div class="container">
                                    <embed src="${pdfData}" type="application/pdf" width="100%" height="100%">
                                    <div class="fallback" style="display: none;">
                                        <p>Your browser doesn't support embedded PDF viewing.</p>
                                        <a href="${pdfData}" download="tax_exemption_certificate.pdf">Download PDF instead</a>
                                    </div>
                                </div>
                                <script>
                                    // Fallback if embed doesn't work
                                    setTimeout(() => {
                                        const embed = document.querySelector('embed');
                                        if (!embed || embed.offsetHeight === 0) {
                                            document.querySelector('.fallback').style.display = 'block';
                                        }
                                    }, 1000);
                                <\/script>
                            </body>
                            </html>
                        `);
                        pdfWindow.document.close();
                    } else {
                        this.$bvToast.toast(
                            "Please allow pop-ups for this site to view PDF.", 
                            {title: "Error", autoHideDelay: 1500, variant:"warning"}
                        );
                    }
                } catch (error) {
                    console.error('Error viewing PDF:', error);
                    this.$bvToast.toast(
                        "Error opening certificate. Please try downloading instead.", 
                        {title: "Error", autoHideDelay: 1500, variant:"warning"}
                    );
                }
            },

            // Download PDF
            downloadPdf(base64String, companyName) {
                try {
                    if (!base64String) {
                        this.$bvToast.toast(
                            "No certificate found to download.", 
                            {title: "Error", autoHideDelay: 1500, variant:"warning"}
                        );
                        return;
                    }

                    // Extract pure base64 data
                    let base64Data = base64String;
                    if (base64String.includes('base64,')) {
                        base64Data = base64String.split('base64,')[1];
                    }

                    // Convert base64 to blob
                    const byteCharacters = atob(base64Data);
                    const byteNumbers = new Array(byteCharacters.length);
                    for (let i = 0; i < byteCharacters.length; i++) {
                        byteNumbers[i] = byteCharacters.charCodeAt(i);
                    }
                    const byteArray = new Uint8Array(byteNumbers);
                    const blob = new Blob([byteArray], { type: 'application/pdf' });

                    // Create download link
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `${companyName || 'certificate'}_tax_exemption.pdf`;
                    document.body.appendChild(link);
                    link.click();
                    
                    // Cleanup
                    setTimeout(() => {
                        document.body.removeChild(link);
                        window.URL.revokeObjectURL(url);
                    }, 100);

                } catch (error) {
                    console.error('Error downloading PDF:', error);
                    this.$bvToast.toast(
                        "Error downloading certificate.", 
                        {title: "Error", autoHideDelay: 1500, variant:"warning"}
                    );
                }
            },

            // Remove PDF
            removePdf(item) {
                item.certificate = null;
                item.pdfFile = null;
                item.showPdfPreview = false;
                item.currentPdfUrl = null;
                
                this.$bvToast.toast(
                    "Certificate removed successfully.", 
                    {title: "Success", autoHideDelay: 1500, variant:"success"}
                );
            },

            remove_finance_company(idx) {
                this.items.splice(idx, 1);
            },

            save_finance_company(item) {
                // If there's a PDF file selected but not yet converted to base64
                if (item.pdfFile && !item.certificate) {
                    // Create a synthetic event to pass to handlePdfUpload
                    const syntheticEvent = {
                        target: {
                            files: [item.pdfFile]
                        }
                    };
                    this.handlePdfUpload(item, syntheticEvent);
                    
                    // Wait for PDF processing before saving
                    setTimeout(() => {
                        this.proceedWithSave(item);
                    }, 1500);
                } else {
                    this.proceedWithSave(item);
                }
            },

            proceedWithSave(item) {
                let path = "/api/finance_company/save_finance_company/"
                let data = {
                    finance_company_id: item.finance_company_id,
                    finance_company_name: item.finance_company_name,
                    finance_company_phone_number: item.finance_company_phone_number,
                    finance_company_website: item.finance_company_website,
                    finance_company_address: item.finance_company_address,
                    finance_company_charges_tax_in_states: item.finance_company_charges_tax_in_states,
                    ein: item.ein,
                    certificate: item.certificate,
                    company_type: item.company_type,
                    password: item.password || null,
                    is_active: item.is_active
                };
                
                let that = this;
                let s = function(response){
                    if (response.data.error){
                        that.$bvToast.toast(
                            response.data.error, 
                            {title: "Error", autoHideDelay: 1500, variant:"warning"}
                        );
                    }
                    else {
                        that.$bvToast.toast(
                            "Tax exempt company saved successfully.", 
                            {title: "Success", autoHideDelay: 1500, variant:"success"}
                        );
                        item.finance_company_id = response.data.result[0];
                        // Clear password field after successful save for security
                        item.password = null;
                        // Clear the base64 data after successful upload
                        if (item.pdfFile) {
                            item.pdfFile = null;
                        }
                    }
                    that.items_being_loaded = false;
                }
                let f = function(error){
                    console.log(error);
                    that.$bvToast.toast(
                        "Error saving tax exempt company. Please try again.", 
                        {title: "Error", autoHideDelay: 1500, variant:"warning"}
                    );
                    that.items_being_loaded = false;
                }
                this.items_being_loaded = true;
                Requestor.postSyncP(path, data, this, s, f);
            },
            
            fetch_init(e) {
                if (e){
                    e.preventDefault();
                }
                let path = "/api/finance_company/get_all_finance_companies/"
                let data = {}
                let that = this;
                let s = function(response){
                    if (response.data.error){
                        that.$bvToast.toast(
                            response.data.error, 
                            {title: "Error", autoHideDelay: 1500, variant:"warning"}
                        );
                    }
                    else {
                        // Filter to only show tax_exempt companies
                        that.items = response.data.result.filter(item => 
                            item.company_type === 'tax_exempt'
                        );
                        // Initialize additional properties
                        that.items.forEach(item => {
                            item.showPassword = false;
                            if (!item.finance_company_charges_tax_in_states) {
                                item.finance_company_charges_tax_in_states = [];
                            }
                            // Set default is_active to true if not present
                            if (item.is_active === undefined || item.is_active === null) {
                                item.is_active = true;
                            }
                        });
                    }
                    that.items_being_loaded = false;
                }
                let f = function(error){
                    console.log(error);
                    that.$bvToast.toast(
                        "Error loading tax exempt companies list. Please refresh.", 
                        {title: "Error", autoHideDelay: 1500, variant:"warning"}
                    );
                    that.items_being_loaded = false;
                }
                this.items_being_loaded = true;
                Requestor.getSyncP(path, data, this, s, f);
            },
            
            add_new() {
                let item = {
                    finance_company_id: null,
                    finance_company_name: null,
                    finance_company_phone_number: null,
                    finance_company_website: null,
                    finance_company_address: null,
                    finance_company_charges_tax_in_states: [],
                    password: null,
                    showPassword: false, 
                    ein: null,
                    certificate: null,
                    company_type: "tax_exempt",
                    ts: null,
                    edit: false,
                    pdfFile: null,
                    showPdfPreview: false,
                    currentPdfUrl: null,
                    is_active: true,
                }
                this.items.unshift(item);
            },
            
            // State selection helpers
            isStateSelected(item, stateValue) {
                return item.finance_company_charges_tax_in_states && 
                       item.finance_company_charges_tax_in_states.includes(stateValue);
            },
            
            toggleState(item, stateValue) {
                if (!item.finance_company_charges_tax_in_states) {
                    item.finance_company_charges_tax_in_states = [];
                }
                
                const index = item.finance_company_charges_tax_in_states.indexOf(stateValue);
                if (index > -1) {
                    item.finance_company_charges_tax_in_states.splice(index, 1);
                } else {
                    item.finance_company_charges_tax_in_states.push(stateValue);
                }
            },
            
            toggleAllStates(item) {
                if (!item.finance_company_charges_tax_in_states) {
                    item.finance_company_charges_tax_in_states = [];
                }
                
                if (this.isAllStatesSelected(item)) {
                    // Clear all
                    item.finance_company_charges_tax_in_states = [];
                } else {
                    // Select all
                    item.finance_company_charges_tax_in_states = this.states.map(state => state.value);
                }
            },
            
            isAllStatesSelected(item) {
                return item.finance_company_charges_tax_in_states && 
                       item.finance_company_charges_tax_in_states.length === this.states.length;
            },
            
            getToggleAllButtonText(item) {
                return this.isAllStatesSelected(item) ? 'Clear All' : 'Select All';
            },
            
            getSelectedStatesPreview(item) {
                if (!item.finance_company_charges_tax_in_states || item.finance_company_charges_tax_in_states.length === 0) {
                    return 'None';
                }
                
                // Show first 5 states, then count
                const firstFive = item.finance_company_charges_tax_in_states.slice(0, 5).join(', ');
                if (item.finance_company_charges_tax_in_states.length <= 5) {
                    return firstFive;
                } else {
                    return `${firstFive}... (+${item.finance_company_charges_tax_in_states.length - 5} more)`;
                }
            }
        },
        created() {
            this.packet = JSON.parse(localStorage.packet);
            this.packet.userDetails = JSON.parse(localStorage.userDetails);
            this.fetch_init();
        }
    }
</script>

<style scoped>
/* Status Styles */
.company-status {
    flex: 0.5;
    min-width: 150px;
}

.status-toggle {
    display: flex;
    align-items: center;
    gap: 10px;
}

.status-switch {
    margin: 0;
}

.status-switch >>> .custom-control-label {
    cursor: pointer;
    font-weight: 600;
    font-size: 13px;
}

.status-text {
    font-weight: 600;
    font-size: 12px;
    margin-left: 8px;
}

.text-success {
    color: #48bb78;
}

.text-danger {
    color: #f56565;
}

/* Update company-header for better spacing */
.company-header {
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
    margin-bottom: 15px;
    padding-bottom: 15px;
    border-bottom: 1px solid #e2e8f0;
    align-items: flex-end;
}

.glass-container {
    min-height: 100vh;
    padding: 20px;
    background-image: url('https://m.media-amazon.com/images/I/81eSegKonhL._AC_SL1500_.jpg');
    background-size: cover;
    background-position: center;
    background-attachment: fixed;
    position: relative;
    overflow: hidden;
}

.glass-container::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.7);
    backdrop-filter: blur(2px);
    z-index: 0;
}

.tax-exempt-companies-container {
    padding: 20px;
    background: transparent;
    min-height: 100vh;
    border-radius: 34px;
    z-index: 1;
    position: relative;
}

.modern-card {
    background: rgba(255, 255, 255, 0.7);
    border-radius: 15px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
    padding: 25px;
    margin: 0 auto;
    margin-top: 10px;
    max-width: 1800px;
}

/* Header */
.header-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 25px;
    padding-bottom: 20px;
    border-bottom: 2px solid #f0f0f0;
}

.title-section .page-title {
    font-size: 1.8rem;
    font-weight: 700;
    color: #2d3748;
    margin: 0;
}

.title-section .page-subtitle {
    color: #718096;
    font-size: 0.95rem;
    margin: 5px 0 0;
}

.action-buttons {
    display: flex;
    gap: 10px;
}

.action-btn {
    padding: 8px 16px;
    font-weight: 600;
    border-radius: 8px;
    transition: all 0.2s ease;
}

.add-btn {
    background: #667eea;
    border: none;
}

.add-btn:hover {
    background: #5a67d8;
    transform: translateY(-1px);
}

.refresh-btn:hover {
    transform: translateY(-1px);
}

/* Info Alert */
.info-alert {
    background: rgba(13, 110, 253, 0.1);
    border: 1px solid rgba(13, 110, 253, 0.2);
    border-radius: 8px;
    margin-bottom: 25px;
}

.alert-content {
    display: flex;
    align-items: flex-start;
}

.alert-content .bi {
    color: #0d6efd;
    margin-top: 2px;
}

/* Loading */
.loading-container {
    text-align: center;
    padding: 40px 20px;
}

/* Company Card */
.company-card {
    background: #ffffff;
    border-radius: 10px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    padding: 20px;
    margin-bottom: 15px;
    border: 1px solid #e2e8f0;
    transition: all 0.3s ease;
}

.company-card:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    border-color: #cbd5e0;
}

/* Company Header */
.company-name {
    flex: 2;
    min-width: 300px;
}

.company-phone {
    flex: 1;
    min-width: 200px;
}

.company-ein {
    flex: 1;
    min-width: 200px;
}

/* Field Label */
.field-label {
    display: block;
    font-size: 11px;
    font-weight: 600;
    color: #718096;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 4px;
}

/* Input Styles */
.name-input,
.phone-input,
.ein-input,
.website-input,
.address-input,
.password-input {
    background: #f8f9fa;
    border-color: #e2e8f0;
    font-size: 12px;
}

.name-input.new-company {
    background: #fff3cd;
    border-color: #ffeaa7;
    font-weight: bold;
}

.name-input:focus,
.phone-input:focus,
.ein-input:focus,
.website-input:focus,
.address-input:focus,
.password-input:focus {
    background: #ffffff;
    border-color: #667eea;
    box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
}

/* Company Details Row */
.company-details-row {
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
    margin-bottom: 15px;
}

.detail-field {
    flex: 1;
    min-width: 200px;
}

.detail-field.address-field {
    flex: 2;
    min-width: 300px;
}

/* Password Section */
.password-section {
    margin-bottom: 20px;
}

.password-field {
    max-width: 400px;
}

.password-input-group {
    display: flex;
    gap: 10px;
    align-items: center;
}

.show-password-toggle {
    margin-top: 24px;
}

.show-password-checkbox {
    font-size: 12px;
    color: #718096;
}

/* Certificate Section */
.certificate-section {
    background: #f8fafc;
    border-radius: 8px;
    padding: 20px;
    margin-bottom: 20px;
    border: 1px solid #e2e8f0;
}

.certificate-header {
    display: flex;
    align-items: center;
    margin-bottom: 15px;
}

.certificate-header .bi {
    color: #e53e3e;
}

.certificate-title {
    font-size: 16px;
    font-weight: 600;
    color: #2d3748;
    margin: 0;
}

.certificate-container {
    background: white;
    border-radius: 6px;
    padding: 15px;
    border: 1px solid #e2e8f0;
}

.certificate-upload {
    text-align: center;
}

.certificate-file-input {
    font-size: 12px;
}

.upload-hint {
    display: block;
    margin-top: 5px;
    color: #a0aec0;
    font-size: 11px;
}

.certificate-actions {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.certificate-buttons {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
}

.certificate-btn {
    font-size: 12px;
    padding: 6px 12px;
}

.preview-btn {
    color: #4299e1;
    border-color: #4299e1;
}

.preview-btn:hover {
    background: #4299e1;
    color: white;
}

.download-btn {
    color: #38a169;
    border-color: #38a169;
}

.download-btn:hover {
    background: #38a169;
    color: white;
}

.remove-btn {
    color: #e53e3e;
    border-color: #e53e3e;
}

.remove-btn:hover {
    background: #e53e3e;
    color: white;
}

.certificate-status {
    display: flex;
    align-items: center;
    gap: 8px;
}

.certificate-uploaded {
    font-size: 12px;
    color: #38a169;
    font-weight: 500;
}

/* Tax Section */
.tax-section {
    background: #f8fafc;
    border-radius: 8px;
    padding: 20px;
    margin-bottom: 20px;
    border: 1px solid #e2e8f0;
}

.tax-header {
    display: flex;
    align-items: center;
    margin-bottom: 10px;
}

.tax-header .bi {
    color: #667eea;
}

.tax-title {
    font-size: 16px;
    font-weight: 600;
    color: #2d3748;
    margin: 0;
}

.tax-description {
    color: #718096;
    font-size: 13px;
    margin-bottom: 15px;
}

/* States Container */
.states-container {
    background: white;
    border-radius: 6px;
    padding: 15px;
    border: 1px solid #e2e8f0;
}

.states-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;
    padding-bottom: 10px;
    border-bottom: 1px solid #e2e8f0;
}

.states-count {
    font-size: 13px;
    font-weight: 600;
    color: #4a5568;
}

.toggle-all-btn {
    font-size: 12px;
    padding: 4px 8px;
}

/* States Grid */
.states-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 10px;
    max-height: 300px;
    overflow-y: auto;
    padding: 10px;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    background: #f9fafb;
}

.state-checkbox-item {
    padding: 8px 10px;
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s ease;
}

.state-checkbox-item:hover {
    border-color: #cbd5e0;
    background: #f7fafc;
}

.state-checkbox-item.selected {
    background: #ebf4ff;
    border-color: #4299e1;
}

.state-checkbox {
    margin: 0;
    width: 100%;
}

.state-checkbox >>> .custom-control-label {
    cursor: pointer;
    width: 100%;
    font-size: 12px;
    color: #4a5568;
}

.state-checkbox-item.selected >>> .custom-control-label {
    color: #2c5282;
    font-weight: 500;
}

.state-label {
    display: inline-block;
    width: 100%;
}

.selected-states-preview {
    margin-top: 15px;
    padding: 10px;
    background: #f0fff4;
    border-radius: 4px;
    border: 1px solid #c6f6d5;
}

.preview-label {
    font-weight: 600;
    color: #276749;
    font-size: 12px;
    margin-right: 8px;
}

.selected-states {
    color: #276749;
    font-size: 12px;
}

/* Company Actions */
.company-actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: 15px;
    border-top: 1px solid #e2e8f0;
}

.company-info {
    display: flex;
    gap: 10px;
    align-items: center;
}

.company-id {
    font-size: 12px;
    color: #718096;
    font-weight: 500;
    background: #f8f9fa;
    padding: 3px 8px;
    border-radius: 4px;
}

.company-type-badge .badge {
    font-size: 11px;
    padding: 4px 8px;
}

.action-buttons-wrapper {
    display: flex;
    gap: 10px;
}

.save-btn {
    background: #48bb78;
    border-color: #48bb78;
    color: white;
    padding: 6px 12px;
    font-size: 12px;
}

.save-btn:hover {
    background: #38a169;
    border-color: #38a169;
}

.save-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.cancel-btn {
    color: #f56565;
    border-color: #f56565;
    padding: 6px 12px;
    font-size: 12px;
}

.cancel-btn:hover {
    background: #f56565;
    color: white;
}

/* Table Footer */
.table-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 15px 0;
    margin-top: 15px;
    border-top: 1px solid #e2e8f0;
}

.pagination-info {
    color: #718096;
    font-size: 0.85rem;
}

/* Empty State */
.empty-state {
    text-align: center;
    padding: 40px 20px;
    border-radius: 8px;
    background: #f8fafc;
    border: 1px dashed #e2e8f0;
}

.empty-state-icon {
    color: #a0aec0;
    margin-bottom: 15px;
}

.empty-state h3 {
    color: #2d3748;
    margin-bottom: 8px;
    font-weight: 600;
}

.empty-state p {
    color: #718096;
    margin-bottom: 15px;
}

.empty-state-actions {
    display: flex;
    gap: 10px;
    justify-content: center;
    flex-wrap: wrap;
}

/* Responsive Design */
@media (max-width: 1200px) {
    .company-header {
        gap: 15px;
    }
    
    .company-name,
    .company-phone,
    .company-ein {
        min-width: calc(33.333% - 10px);
    }
    
    .states-grid {
        grid-template-columns: repeat(auto-fill, minmax(99px, 1fr));
    }
}

@media (max-width: 768px) {
    .modern-card {
        padding: 15px;
    }
    
    .header-section {
        flex-direction: column;
        align-items: flex-start;
        gap: 15px;
    }
    
    .action-buttons {
        width: 100%;
    }
    
    .action-btn {
        flex: 1;
        justify-content: center;
    }
    
    .company-header {
        flex-direction: column;
        gap: 15px;
    }
    
    .company-name,
    .company-phone,
    .company-ein {
        width: 100%;
        min-width: unset;
    }
    
    .company-status {
        width: 100%;
        min-width: unset;
    }
    
    .company-details-row {
        flex-direction: column;
        gap: 15px;
    }
    
    .detail-field,
    .detail-field.address-field {
        width: 100%;
        min-width: unset;
    }
    
    .password-input-group {
        flex-direction: column;
        align-items: flex-start;
    }
    
    .show-password-toggle {
        margin-top: 5px;
    }
    
    .certificate-buttons {
        flex-direction: column;
    }
    
    .certificate-btn {
        width: 100%;
    }
    
    .states-grid {
        grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
    }
    
    .company-actions {
        flex-direction: column;
        gap: 15px;
        align-items: stretch;
    }
    
    .action-buttons-wrapper {
        justify-content: flex-end;
    }
}

@media (max-width: 480px) {
    .action-buttons {
        flex-direction: column;
    }
    
    .action-btn {
        width: 100%;
        justify-content: center;
    }
    
    .states-header {
        flex-direction: column;
        gap: 10px;
        align-items: flex-start;
    }
    
    .company-actions {
        align-items: stretch;
    }
    
    .action-buttons-wrapper {
        flex-direction: column;
    }
    
    .save-btn,
    .cancel-btn {
        width: 100%;
    }
}
</style>


// WEBPACK FOOTER //
// src/components/ControlPanel/TaxExempt.vue